import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@mellify/ui';
import { useSidebarStore } from '../../stores/sidebarStore';
import { Sidebar } from './Sidebar';

export interface MobileMenuProps {
  /** Currently active route path for highlighting */
  activePath?: string;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ activePath }) => {
  const { isMobileOpen, closeMobile } = useSidebarStore();

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isMobileOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMobile();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileOpen, closeMobile]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-overlay bg-dark-900/50 dark:bg-black/70 backdrop-blur-sm transition-opacity duration-300 lg:hidden',
          isMobileOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        )}
        onClick={closeMobile}
        aria-hidden="true"
      />

      {/* Sidebar panel */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-modal flex w-72 transition-transform duration-300 ease-smooth lg:hidden',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        role="dialog"
        aria-modal={isMobileOpen}
        aria-label="Navigation menu"
      >
        <div className="relative flex w-full flex-col bg-white dark:bg-dark-900 shadow-2xl">
          {/* Close button */}
          <button
            type="button"
            onClick={closeMobile}
            className="absolute top-3 right-3 z-10 inline-flex items-center justify-center p-1.5 rounded-md text-dark-400 hover:text-dark-600 dark:text-dark-500 dark:hover:text-dark-300 hover:bg-dark-100 dark:hover:bg-dark-700 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            aria-label="Close navigation menu"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Force sidebar expanded on mobile overlay */}
          <div className="flex-1 overflow-hidden">
            <MobileSidebarContent activePath={activePath} />
          </div>
        </div>
      </div>
    </>
  );
};

/**
 * A wrapper that renders the Sidebar always in expanded mode for the mobile overlay.
 */
const MobileSidebarContent: React.FC<{ activePath?: string }> = ({ activePath }) => {
  const { closeMobile } = useSidebarStore();

  return (
    <div className="h-full" onClick={closeMobile}>
      <Sidebar activePath={activePath} className="!w-full" />
    </div>
  );
};

MobileMenu.displayName = 'MobileMenu';